import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';

// CRA bundling breaks Leaflet's default icon URL resolution — point it at the bundled assets
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const DEFAULT_CENTER = [32.0853, 34.7818]; // Tel Aviv — most gardens are in Israel
const PIN_ZOOM = 16;
const NO_PIN_ZOOM = 7;

// Valid numeric coordinates or null. (0,0) is treated as "not set" — it is the
// empty-form default and never a real garden location.
const toCoords = (latitude, longitude) => {
  const lat = parseFloat(latitude);
  const lng = parseFloat(longitude);
  if (Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0)) {
    return [lat, lng];
  }
  return null;
};

// Recenters when the coordinates change (e.g. typed into the text fields or
// found via search), and fixes the size-0 render inside an animating Dialog.
const MapController = ({ coords }) => {
  const map = useMap();
  const lat = coords ? coords[0] : null;
  const lng = coords ? coords[1] : null;

  useEffect(() => {
    const timer = setTimeout(() => map.invalidateSize(), 250);
    return () => clearTimeout(timer);
  }, [map]);

  useEffect(() => {
    if (lat !== null && lng !== null) {
      map.setView([lat, lng], Math.max(map.getZoom(), PIN_ZOOM));
    }
  }, [map, lat, lng]);

  return null;
};

const ClickToPlacePin = ({ onLocationChange }) => {
  useMapEvents({
    click: (e) => onLocationChange(e.latlng.lat, e.latlng.lng),
  });
  return null;
};

/**
 * OpenStreetMap-based location picker (no API key required).
 *
 * Interactive mode: click the map or drag the pin to set coordinates, or search
 * an address via Nominatim. Read-only mode: just shows the pin.
 *
 * Props:
 * - latitude / longitude: current values (string or number, as held in form state)
 * - onLocationChange(lat, lng): called with numbers when the user picks a point
 * - readOnly: disables pin editing and hides the search box
 * - height: map height in px
 */
const LocationPickerMap = ({ latitude, longitude, onLocationChange, readOnly = false, height = 300 }) => {
  const coords = toCoords(latitude, longitude);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchMessage, setSearchMessage] = useState('');

  const handleSearch = async () => {
    const query = searchQuery.trim();
    if (!query || searching) {
      return;
    }
    try {
      setSearching(true);
      setSearchMessage('');
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(query)}`,
        { headers: { Accept: 'application/json' } }
      );
      if (!response.ok) {
        throw new Error(`Search failed (${response.status})`);
      }
      const results = await response.json();
      if (Array.isArray(results) && results.length > 0) {
        const { lat, lon, display_name: displayName } = results[0];
        onLocationChange(parseFloat(lat), parseFloat(lon));
        setSearchMessage(displayName);
      } else {
        setSearchMessage('No results found');
      }
    } catch (err) {
      console.error('Address search failed:', err);
      setSearchMessage(`Address search failed: ${err.message}`);
    } finally {
      setSearching(false);
    }
  };

  return (
    <Box>
      {!readOnly && (
        <TextField
          fullWidth
          size="small"
          placeholder="Search address or place…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSearch();
            }
          }}
          helperText={searchMessage || ' '}
          sx={{ mb: 1 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {searching ? (
                  <CircularProgress size={20} />
                ) : (
                  <IconButton size="small" onClick={handleSearch} aria-label="search address">
                    <SearchIcon />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
        />
      )}
      <Box sx={{ height, borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
        <MapContainer
          center={coords || DEFAULT_CENTER}
          zoom={coords ? PIN_ZOOM : NO_PIN_ZOOM}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={!readOnly}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapController coords={coords} />
          {!readOnly && <ClickToPlacePin onLocationChange={onLocationChange} />}
          {coords && (
            <Marker
              position={coords}
              draggable={!readOnly}
              eventHandlers={
                readOnly
                  ? undefined
                  : {
                      dragend: (e) => {
                        const point = e.target.getLatLng();
                        onLocationChange(point.lat, point.lng);
                      },
                    }
              }
            />
          )}
        </MapContainer>
      </Box>
      {!readOnly && (
        <Typography variant="caption" color="text.secondary">
          Click the map or drag the pin to set the location.
        </Typography>
      )}
    </Box>
  );
};

export default LocationPickerMap;
